import React from "react";

import useEmblaCarousel from "embla-carousel-react";
import { useNavigate } from "react-router-dom";

import { activateOnEnterOrSpace } from "../utils/keyboardActivation";

const parseEventTimestamp = (value) => {
  if (value == null) return null;
  if (typeof value === "number") {
    const ms = value < 1e12 ? value * 1000 : value;

    return new Date(ms);
  }
  const ts = String(value);
  const normalized =
    ts.endsWith("Z") || ts.includes("+") || ts.includes("-") ? ts : `${ts}Z`;

  return new Date(normalized);
};

const UpcomingEventsCarousel = ({ events = [] }) => {
  const navigate = useNavigate();

  // Filter events that are from now onwards (future events) and sort by timestamp
  const now = new Date(); // User's local time

  // First filter out any null/undefined events and ensure they have required properties
  const validEvents = events.filter((event) => {
    if (!event) return false;

    // Check if event has at least some identifying properties
    return (
      event &&
      (event.mediaId ||
        event.id ||
        event.idEvent ||
        event.strEvent ||
        event.name)
    );
  });

  const filteredAndSortedEvents = validEvents
    .filter((event) => {
      // Parse UTC timestamps to local time
      let eventTime = null;

      if (event.strTimestamp) {
        // strTimestamp is ISO-8601 format (UTC) - it may or may not have Z suffix
        const timestampStr =
          event.strTimestamp.endsWith("Z") ||
          event.strTimestamp.includes("+") ||
          event.strTimestamp.includes("-")
            ? event.strTimestamp
            : `${event.strTimestamp}Z`;

        eventTime = new Date(timestampStr);
      } else if (event.timestamp != null) {
        eventTime = parseEventTimestamp(event.timestamp);
      } else if (event.dateEvent && event.strTime) {
        // Fallback: combine dateEvent and strTime as UTC
        const combinedDateTime = `${event.dateEvent}T${event.strTime}Z`;

        eventTime = new Date(combinedDateTime);
      }

      if (!eventTime || isNaN(eventTime.getTime())) return false;

      return eventTime >= now;
    })
    .sort((a, b) => {
      // Parse timestamps for sorting
      let timeA = null;
      let timeB = null;

      if (a.strTimestamp) {
        const timestampStr =
          a.strTimestamp.endsWith("Z") ||
          a.strTimestamp.includes("+") ||
          a.strTimestamp.includes("-")
            ? a.strTimestamp
            : `${a.strTimestamp}Z`;

        timeA = new Date(timestampStr).getTime();
      } else if (a.timestamp != null) {
        timeA = parseEventTimestamp(a.timestamp)?.getTime();
      } else if (a.dateEvent && a.strTime) {
        timeA = new Date(`${a.dateEvent}T${a.strTime}Z`).getTime();
      }

      if (b.strTimestamp) {
        const timestampStr =
          b.strTimestamp.endsWith("Z") ||
          b.strTimestamp.includes("+") ||
          b.strTimestamp.includes("-")
            ? b.strTimestamp
            : `${b.strTimestamp}Z`;

        timeB = new Date(timestampStr).getTime();
      } else if (b.timestamp != null) {
        timeB = parseEventTimestamp(b.timestamp)?.getTime();
      } else if (b.dateEvent && b.strTime) {
        timeB = new Date(`${b.dateEvent}T${b.strTime}Z`).getTime();
      }

      return (timeA || 0) - (timeB || 0);
    });

  const sortedEvents = filteredAndSortedEvents;

  const [emblaRef] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
    containScroll: "trimSnaps",
    dragFree: true,
  });

  // Don't render if no valid events after filtering
  if (!validEvents || validEvents.length === 0) return null;

  const renderEventCard = (event) => {
    // Safety check for undefined/null events
    if (!event) {
      return null;
    }

    // Check for required properties
    if (!event.mediaId && !event.id && !event.idEvent) {
      return null;
    }

    // Parse UTC timestamp to local time
    let eventDate = null;

    if (event.strTimestamp) {
      // strTimestamp is ISO-8601 format (UTC) - it may or may not have Z suffix
      const timestampStr =
        event.strTimestamp.endsWith("Z") ||
        event.strTimestamp.includes("+") ||
        event.strTimestamp.includes("-")
          ? event.strTimestamp
          : `${event.strTimestamp}Z`;

      eventDate = new Date(timestampStr);
    } else if (event.timestamp != null) {
      eventDate = parseEventTimestamp(event.timestamp);
    } else if (event.dateEvent && event.strTime) {
      // Fallback: combine dateEvent and strTime as UTC
      const combinedDateTime = `${event.dateEvent}T${event.strTime}Z`;

      eventDate = new Date(combinedDateTime);
    }

    // Format time for the pill
    const formatTimeForPill = () => {
      if (!eventDate) return "";

      const today = new Date();
      const isToday = eventDate.toDateString() === today.toDateString();
      const tomorrow = new Date(today);

      tomorrow.setDate(tomorrow.getDate() + 1);
      const isTomorrow = eventDate.toDateString() === tomorrow.toDateString();

      const timeStr = eventDate
        .toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
        .toUpperCase();

      if (isToday) {
        return `TODAY ${timeStr}`;
      } else if (isTomorrow) {
        return `TOMORROW ${timeStr}`;
      } else {
        // Show day of week for next 7 days
        const daysDiff = Math.floor(
          (eventDate - today) / (1000 * 60 * 60 * 24)
        );

        if (daysDiff <= 7 && daysDiff > 0) {
          const dayName = eventDate
            .toLocaleDateString("en-US", { weekday: "short" })
            .toUpperCase();

          return `${dayName} ${timeStr}`;
        } else {
          // Show date for events further out
          const dateStr = eventDate
            .toLocaleDateString("en-US", { month: "short", day: "numeric" })
            .toUpperCase();

          return `${dateStr} ${timeStr}`;
        }
      }
    };

    const homeTeam = event.homeTeam || event.strHomeTeam || "";
    const awayTeam = event.awayTeam || event.strAwayTeam || "";

    return (
      <div className="cursor-pointer group">
        <div
          className="relative rounded-lg overflow-hidden transition-transform duration-300 hover:scale-105 aspect-video"
          role="button"
          style={{
            boxShadow:
              "0 15px 35px -15px rgba(0, 0, 0, 0.3), 0 10px 20px -10px rgba(0, 0, 0, 0.2)",
          }}
          tabIndex={0}
          onClick={() =>
            navigate(
              `/web-player/sports/event/${event.idEvent || event.mediaId || event.id}`,
              {
                state: { event },
              }
            )
          }
          onKeyDown={activateOnEnterOrSpace}
        >
          <img
            alt={
              event.name ||
              event.strEvent ||
              (event.homeTeam && event.awayTeam
                ? `${event.homeTeam} vs ${event.awayTeam}`
                : "") ||
              (event.strHomeTeam && event.strAwayTeam
                ? `${event.strHomeTeam} vs ${event.strAwayTeam}`
                : "") ||
              "Sports Event"
            }
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            src={
              event.posterPath ||
              event.poster ||
              event.strThumb ||
              event.backdropPath ||
              event.thumbnail ||
              event.strPostponed ||
              event.strSquare ||
              event.strBanner ||
              "/background.png"
            }
            onError={(e) => {
              // Try backdropPath as fallback if main image fails
              if (event.backdropPath && e.target.src !== event.backdropPath) {
                e.target.src = event.backdropPath;
              } else {
                e.target.src = "/background.png";
              }
            }}
          />

          {/* Time Pill - Top Left */}
          {eventDate && (
            <div className="absolute top-3 left-3 bg-white text-black text-[10px] font-bold px-2.5 py-1 rounded-full z-10">
              {formatTimeForPill()}
            </div>
          )}

          {/* LIVE Badge - Top Right */}
          {(event.status === "LIVE" || event.status === "Live") && (
            <div className="absolute top-3 right-3 bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full animate-pulse z-10">
              LIVE
            </div>
          )}
        </div>

        {/* Team Names */}
        {homeTeam && awayTeam && (
          <div className="mt-2 px-1 overflow-hidden relative">
            <div className="text-white/80 text-sm truncate group-hover:opacity-0 hover:opacity-0 transition-opacity">
              <span className="font-bold">{homeTeam}</span> vs{" "}
              <span className="font-bold">{awayTeam}</span>
            </div>
            <div className="text-white/80 text-sm whitespace-nowrap absolute top-0 left-0 opacity-0 group-hover:opacity-100 hover:opacity-100 group-hover:animate-scroll-left hover:animate-scroll-left px-1">
              <span className="font-bold">{homeTeam}</span> vs{" "}
              <span className="font-bold">{awayTeam}</span>
              <span className="inline-block ml-8">
                <span className="font-bold">{homeTeam}</span> vs{" "}
                <span className="font-bold">{awayTeam}</span>
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleViewMoreClick = () => {
    // Navigate to the upcoming events page
    navigate("/web-player/sports/events/upcoming");
  };

  const renderViewMoreCard = () => (
    <div
      className="relative group cursor-pointer rounded-lg overflow-hidden transition-transform duration-300 hover:scale-105"
      role="button"
      tabIndex={0}
      onClick={handleViewMoreClick}
      onKeyDown={activateOnEnterOrSpace}
    >
      <div className="aspect-video relative flex items-center justify-center">
        {/* Icon and Text */}
        <div className="text-center">
          <i className="fa-solid fa-rectangle-history text-6xl text-white/40 group-hover:text-white/60 transition-colors duration-300 mb-3" />
          <div className="text-white/80 text-lg font-semibold">View More</div>
          <div className="text-white/60 text-sm mt-1">
            Browse all upcoming events
          </div>
        </div>
      </div>
    </div>
  );

  // If no events to show, don't render the carousel
  if (!sortedEvents || sortedEvents.length === 0) {
    return null;
  }

  return (
    <div className="relative pb-6">
      <div className="overflow-hidden py-4 -mt-4 px-2" ref={emblaRef}>
        <div className="flex gap-4">
          {sortedEvents.map((event, index) => {
            // Skip if event is somehow null/undefined (safety check)
            if (!event) {
              return null;
            }

            return (
              <div
                className="flex-[0_0_18.18%] min-w-0"
                key={
                  event.mediaId || event.id || event.idEvent || `event-${index}`
                }
              >
                {renderEventCard(event)}
              </div>
            );
          })}
          {/* View More Card - Always show as last card */}
          <div className="flex-[0_0_18.18%] min-w-0">
            {renderViewMoreCard()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpcomingEventsCarousel;
